import React from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import Message from 'components/Message';
import { intlMessage } from 'constants/propTypes';
import { SvgArrow } from 'components/Svgs';
import { TooltipContent, TooltipToggle } from 'components/Tooltip';
import { ICONS } from 'constants/icons';
import { useCollapseState } from 'features/shared/useCollapseState';
import { bem } from 'helpers/class-name';

import { skuMenuSectionMessages } from '../SkuItems.messages';
import { isUncategorizedCategory } from '../SkuMenuData.normalizers';

import SkuCategorySection from './SkuCategorySection';

const { block, element } = bem('sku-menu-section');

function SkuMenuSection({
  menu,
  noCategoriesMessage,
  hideMenuTitle,
  currencyCode,
  collapseStorageKey,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onEditCategory,
  onDuplicateCategory,
  onToggleCategory,
  onStatusChange,
  onViewItem,
  onViewCategory,
}) {
  const intl = useIntl();
  const isDisabled = menu.enabled === false;
  const [isOpen, setIsOpen] = useCollapseState(collapseStorageKey, `menu-${menu.id}`, !isDisabled);
  const categories = menu.categories || [];
  const shouldRenderContent = hideMenuTitle || isOpen;
  const panelId = `sku-menu-panel-${menu.id}`;
  const disabledTooltipName = `sku-menu-disabled-${menu.id}`;

  return (
    <section className={block()}>
      {!hideMenuTitle && (
        <>
          <button
            type="button"
            className={element('header')}
            data-testid={`sku-menu-toggle-${menu.id}`}
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => setIsOpen(open => !open)}>
            <span className={element('arrow')}>
              <SvgArrow direction={isOpen ? 'down' : 'right'} theme="dark" />
            </span>
            <h3 className={element('title', { disabled: isDisabled })}>{menu.name}</h3>
            {isDisabled && (
              <TooltipToggle
                name={disabledTooltipName}
                className={element('disabled-indicator')}
                aria-label={intl.formatMessage(skuMenuSectionMessages.disabledMenuTooltip)}>
                <Isvg src={ICONS.eyeWithCross} />
              </TooltipToggle>
            )}
          </button>
          {isDisabled && (
            <TooltipContent name={disabledTooltipName} place="bottom">
              <Message message={skuMenuSectionMessages.disabledMenuTooltip} />
            </TooltipContent>
          )}
        </>
      )}
      <div id={panelId} hidden={!shouldRenderContent} aria-hidden={!shouldRenderContent}>
        {categories.length > 0 ? (
          <div className={element('categories')}>
            {categories.map(category => {
              const isSystemCategory = isUncategorizedCategory(category);
              return (
                <SkuCategorySection
                  key={category.id}
                  category={category}
                  currencyCode={currencyCode}
                  collapseStorageKey={collapseStorageKey}
                  onAddItem={onAddItem && (() => onAddItem(category, menu))}
                  onEditItem={onEditItem && ((item, selectedCategory) => onEditItem(item, selectedCategory, menu))}
                  onDeleteItem={
                    onDeleteItem && ((item, selectedCategory) => onDeleteItem(item, selectedCategory, menu))
                  }
                  onEditCategory={isSystemCategory ? undefined : onEditCategory}
                  onDuplicateCategory={isSystemCategory ? undefined : onDuplicateCategory}
                  onToggleCategory={isSystemCategory ? undefined : onToggleCategory}
                  onStatusChange={onStatusChange}
                  onViewItem={onViewItem && ((item, selectedCategory) => onViewItem(item, selectedCategory, menu))}
                  onViewCategory={isSystemCategory ? undefined : onViewCategory}
                />
              );
            })}
          </div>
        ) : (
          <div className={element('empty')}>
            <Message message={noCategoriesMessage} />
          </div>
        )}
      </div>
    </section>
  );
}

SkuMenuSection.propTypes = {
  menu: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    enabled: PropTypes.bool,
    categories: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })).isRequired,
  }).isRequired,
  noCategoriesMessage: intlMessage.isRequired,
  hideMenuTitle: PropTypes.bool,
  currencyCode: PropTypes.string,
  collapseStorageKey: PropTypes.string.isRequired,
  onAddItem: PropTypes.func,
  onEditItem: PropTypes.func,
  onDeleteItem: PropTypes.func,
  onEditCategory: PropTypes.func,
  onDuplicateCategory: PropTypes.func,
  onToggleCategory: PropTypes.func,
  onStatusChange: PropTypes.func,
  onViewItem: PropTypes.func,
  onViewCategory: PropTypes.func,
};

SkuMenuSection.defaultProps = {
  hideMenuTitle: false,
};

export default SkuMenuSection;
